import { useState, type ChangeEvent, type ReactNode } from 'react';
import { useT } from '../../i18n/i18n';
import { Icon } from './Icon';
import { SheetShell } from './Sheet';

// Grouped form rows (Owner-selected design 2026-09-14, Figma 191:7 "NEW · B" and 191:10
// "AGREEMENT · B"): one grey row per fact — label on the left, the value on the right — in the iOS
// Settings idiom. Four kinds:
//   RowInput    the value is typed in place (right-aligned; placeholder while empty)
//   PickerRow   › opens a bottom option sheet; tapping the chosen option again clears it (never a default)
//   SwitchRow   an on / off switch (role="switch")
//   CheckRow    a tick box with its sentence (role="checkbox")
// Every row is at least 48px tall; a sheet always has Close and the backdrop as its way out (OC §9.4).

export function FormRow(props: { label: ReactNode; children?: ReactNode; className?: string; testId?: string }) {
  return (
    <div className={`form-row ${props.className ?? ''}`} data-testid={props.testId}>
      <span className="form-row-label">{props.label}</span>
      {props.children}
    </div>
  );
}

export function RowInput(props: {
  id?: string;
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel' | 'number';
  inputMode?: 'text' | 'numeric' | 'email' | 'tel' | 'decimal';
  disabled?: boolean;
  autoComplete?: string;
  error?: boolean;
  testId?: string;
}) {
  return (
    <label className={`form-row form-row-input ${props.disabled ? 'form-row-disabled' : ''} ${props.error ? 'form-row-error' : ''}`} htmlFor={props.id}>
      <span className="form-row-label">{props.label}</span>
      <input
        id={props.id}
        className="form-row-field"
        type={props.type ?? 'text'}
        inputMode={props.inputMode}
        value={props.value}
        placeholder={props.placeholder}
        disabled={props.disabled}
        autoComplete={props.autoComplete}
        onChange={(event: ChangeEvent<HTMLInputElement>) => props.onChange(event.target.value)}
        data-testid={props.testId}
      />
    </label>
  );
}

export interface PickerOption<K extends string> {
  key: K;
  label: string;
  testId?: string;
}

export function PickerRow<K extends string>(props: {
  label: ReactNode;
  value: K | null;
  options: PickerOption<K>[];
  onChange: (key: K | null) => void;
  // What the row shows while nothing is chosen (default 未设置 / Not set).
  placeholder?: string;
  // Override the shown value (e.g. "5 户及以上" for any count ≥ 5).
  display?: string;
  sheetTitle?: string;
  disabled?: boolean;
  testId?: string;
  valueTestId?: string;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const current = props.options.find((o) => o.key === props.value) ?? null;
  const shown = props.display ?? current?.label ?? null;
  return (
    <>
      <button
        type="button"
        className={`form-row form-row-picker ${props.disabled ? 'form-row-disabled' : ''}`}
        onClick={() => !props.disabled && setOpen(true)}
        disabled={props.disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={props.testId}
        data-value={props.value ?? ''}
      >
        <span className="form-row-label">{props.label}</span>
        <span className={`form-row-value ${shown === null ? 'form-row-placeholder' : ''}`} data-testid={props.valueTestId}>
          {shown ?? props.placeholder ?? t('common.notSet')}
        </span>
        <Icon name="chevron_right" className="form-row-chevron" size={20} />
      </button>
      {open ? (
        <SheetShell title={props.sheetTitle ?? (typeof props.label === 'string' ? props.label : t('common.select'))} onClose={() => setOpen(false)} testId={props.testId ? `${props.testId}-sheet` : undefined}>
          <div className="option-list" role="listbox">
            {props.options.map((option) => {
              const selected = option.key === props.value;
              return (
                <button
                  key={option.key}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`option-row ${selected ? 'option-row-selected' : ''}`}
                  onClick={() => {
                    props.onChange(selected ? null : option.key);
                    setOpen(false);
                  }}
                  data-testid={option.testId}
                >
                  <span>{option.label}</span>
                  {selected ? <Icon name="check" /> : null}
                </button>
              );
            })}
          </div>
        </SheetShell>
      ) : null}
    </>
  );
}

export function SwitchRow(props: { label: ReactNode; on: boolean; onToggle: (on: boolean) => void; disabled?: boolean; tone?: 'default' | 'ai'; testId?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={props.on}
      className={`form-row form-row-switch ${props.tone === 'ai' ? 'form-row-ai' : ''} ${props.disabled ? 'form-row-disabled' : ''}`}
      onClick={() => !props.disabled && props.onToggle(!props.on)}
      disabled={props.disabled}
      data-testid={props.testId}
      data-on={props.on ? 'true' : 'false'}
    >
      <span className="form-row-label form-row-label-main">{props.label}</span>
      <span className={`switch ${props.tone === 'ai' ? 'switch-ai' : ''}`} aria-hidden="true">
        <span className="switch-knob" />
      </span>
    </button>
  );
}

export function CheckRow(props: { label: ReactNode; checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean; testId?: string }) {
  return (
    <button type="button" role="checkbox" aria-checked={props.checked} className="check-row" onClick={() => !props.disabled && props.onChange(!props.checked)} disabled={props.disabled} data-testid={props.testId}>
      <span className="check-box" aria-hidden="true">
        {props.checked ? <Icon name="check" size={20} /> : null}
      </span>
      <span className="check-row-label">{props.label}</span>
    </button>
  );
}
